// Formatting helpers for bytes, durations, percents, and counts.
//
// These helpers are read-only, pure, and unit-testable — every page
// that displays a numeric KPI (Overview, Host, Containers) renders
// its values through them so a single unit-test guards the
// human-readable formatting across the SPA.
//
// Conventions:
//   - Bytes use IEC binary units (KiB, MiB, GiB, TiB). Memory and
//     disk sizes are powers of two on Linux.
//   - Durations larger than 60 seconds roll up to `Xm Ys`,
//     `Xh Ym`, `Xd Yh`. The largest unit is days; longer durations
//     are intentionally shown as `Xd Yh` rather than collapsing to a
//     single number so the operator can scan uptime at a glance.
//   - Uptime is expressed as a percentage of a 30-day rolling
//     window, matching the dashboard's SLO.
//   - All inputs are clamped to non-negative numbers — a missing
//     host metric must never produce `-7%` or `-3 B` in the UI.

const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const SECONDS_PER_DAY = HOURS_PER_DAY * MINUTES_PER_HOUR * SECONDS_PER_MINUTE;
const MS_PER_SECOND = 1000;

const UPTIME_BASELINE_DAYS = 30;
const UPTIME_BASELINE_SECONDS = UPTIME_BASELINE_DAYS * SECONDS_PER_DAY;

export interface FormatOptions {
  /** Number of decimal places to render. Default 0 for percent, 1 for bytes/duration. */
  precision?: number;
}

const BYTE_UNITS: ReadonlyArray<readonly [string, number]> = [
  ["B", 1],
  ["KiB", 1024],
  ["MiB", 1024 ** 2],
  ["GiB", 1024 ** 3],
  ["TiB", 1024 ** 4],
];

/** Render a byte count using IEC units (B / KiB / MiB / GiB / TiB). */
export function formatBytes(bytes: number, opts: FormatOptions = {}): string {
  const safe = Math.max(0, bytes);
  if (safe < 1) return "0 B";
  // Find the largest unit whose base fits the value.
  let unitIdx = 0;
  for (let i = BYTE_UNITS.length - 1; i >= 0; i -= 1) {
    if (safe >= BYTE_UNITS[i]![1]) {
      unitIdx = i;
      break;
    }
  }
  const [, base] = BYTE_UNITS[unitIdx]!;
  const value = safe / base;
  // Single-byte values render as "1 B" — no decimals. IEC units ≥ KiB
  // use one decimal below 10, two decimals above.
  const precision =
    opts.precision ?? (unitIdx === 0 ? 0 : defaultBytePrecision(value));
  return `${value.toFixed(precision)} ${BYTE_UNITS[unitIdx]![0]}`;
}

/** Render a percent value in [0, 100], clamped. */
export function formatPercent(value: number, opts: FormatOptions = {}): string {
  const safe = clamp(value, 0, 100);
  const precision = opts.precision ?? 0;
  return `${safe.toFixed(precision)}%`;
}

/** Render a duration (ms) with adaptive units: ms / s / m+s / h+m / d+h. */
export function formatDuration(ms: number): string {
  const safe = Math.max(0, ms);
  if (safe < MS_PER_SECOND) return `${Math.round(safe)}ms`;

  const totalSeconds = Math.floor(safe / MS_PER_SECOND);
  const days = Math.floor(totalSeconds / SECONDS_PER_DAY);
  const hours = Math.floor(
    (totalSeconds % SECONDS_PER_DAY) / (MINUTES_PER_HOUR * SECONDS_PER_MINUTE),
  );
  const minutes = Math.floor(
    (totalSeconds % (MINUTES_PER_HOUR * SECONDS_PER_MINUTE)) / SECONDS_PER_MINUTE,
  );
  const seconds = totalSeconds % SECONDS_PER_MINUTE;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;

  // < 1 minute: render seconds with one decimal so sub-second
  // timings stay readable (e.g. "1.5s" vs "2s").
  const value = safe / MS_PER_SECOND;
  return `${value.toFixed(1)}s`;
}

/** Render uptime in seconds as a percentage of the 30-day baseline. */
export function formatUptime(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return "0%";
  if (totalSeconds >= UPTIME_BASELINE_SECONDS) return "100%";
  return formatPercent((totalSeconds / UPTIME_BASELINE_SECONDS) * 100);
}

/** Render an integer count with a thousands separator. */
export function formatNumber(value: number): string {
  const safe = Math.max(0, Math.round(value));
  // Intl is overkill — a manual separator handles up to 1e15 in O(n).
  const s = String(safe);
  const out: string[] = [];
  for (let i = s.length; i > 0; i -= 3) {
    out.unshift(s.slice(Math.max(0, i - 3), i));
  }
  return out.join(",");
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, value));
}

/**
 * Pick a precision that strips trailing zeros for sub-10 values while
 * keeping two-decimal stability above 10 — matches how `df -h` and
 * most Linux dashboards display sizes.
 */
function defaultBytePrecision(value: number): number {
  return value < 10 ? 1 : 2;
}
